import jsPDF from 'jspdf';
import {
    KAPITEL,
    baueVerzeichnis,
    gemeinsameEinrichtungen,
    kapitelBefunde,
    koordinierungsSchluessel,
    nachweisUebersicht,
    offeneNachweise,
    schritteDesGewerks,
    seitenPlan,
    sperrHinweis,
    wirksamerZeitraumDerFassung,
} from '../../components/handbuch/handbuchLogic';
import { ORG_MODELLE, ROLLE_LABEL, orgModellLabel } from '../../components/handbuch/orgModelle';
import type { OrgStrukturKnoten } from '../../components/handbuch/orgModelle';
import { sichereTextausgabe, winAnsiSicher } from './winAnsi';
import type {
    GewerkAusPlan,
    HandbuchGewerk,
    HandbuchLage,
    HandbuchNachweis,
    KapitelId,
} from '../../components/handbuch/types';
import type { SeitenEintrag } from '../../components/handbuch/handbuchLogic';
import { UNBEKANNT } from '../../components/koordinierung/gewerkeKatalog';
import { baueZeitachse, paarKey } from '../../components/koordinierung/koordinierungLogic';
import type {
    GewerkAusPlan as KoordinierungsGewerk,
    KoordinierungPaar,
} from '../../components/koordinierung/koordinierungLogic';
import { alsTag, ampel, berechneAlleUeberschneidungen, berechneUeberschneidung } from '../../components/koordinierung/ueberschneidung';
import { kategorieLabel } from '../../components/dokumente/dokumenteLogic';
import type { ProjektDokument } from '../../components/dokumente/types';
import type {
    Arbeitsschritt,
    EinweisungQrQuelle,
    Gefaehrdung,
    NotrufplanInhalt,
    Schutzmassnahme,
} from '../../components/sige-plan/types';

/**
 * Das SiGe-Handbuch als eigenes PDF.
 *
 * ═══ EIN EIGENES DOKUMENT, KEIN ANHANG ════════════════════════════════════
 * Entscheidung Nr. 3: das Handbuch wird NICHT an den Plotterplan gehaengt und
 * geht nicht durch `mergePdf.ts`. Plan und Handbuch sind zwei Dokumente mit
 * zwei Zwecken und zwei Lebenslaeufen — der Plan haengt am Bauzaun und wird
 * bei jeder Terminaenderung neu geplottet, das Handbuch liegt im Bauburo und
 * wird fassungsweise freigegeben. Ein zusammengeklebtes PDF haette immer eine
 * der beiden Fassungen falsch.
 *
 * ═══ A3 QUER ══════════════════════════════════════════════════════════════
 * Wie die Bildschirmseiten des Moduls (`kapitel/stil.ts`: `quer` = 420 × 297)
 * und wie der Muster-SiGe-Plan, aus dem die Vorlage stammt. Ein Kapitel ist
 * eine Tafel, kein Fliesstext; die Gefaehrdungsmatrix und der Gesamtzeitplan
 * brauchen die Breite.
 *
 * ═══ EINE SEITE JE EINTRAG DES SEITENSPIEGELS ═════════════════════════════
 * Die Seitenzaehlung kommt aus `seitenPlan()` in `handbuchLogic.ts` und wird
 * hier NICHT nachgerechnet. Der Grund steht dort: Kopf und Fuss tragen
 * „Seite x von y", und y muss feststehen, bevor die erste Seite gezeichnet
 * ist. Waeren es zwei Zaehlungen — eine fuer die Vorschau, eine fuers PDF —,
 * kaemen sie irgendwann auseinander.
 *
 * Daraus folgt die wichtigste Bauregel dieses Generators: **ein Kapitel darf
 * nicht umbrechen.** Was nicht auf die Tafel passt, wird abgeschnitten und der
 * Schnitt ausdruecklich vermerkt („… und 7 weitere"). Genau so verhaelt sich
 * die Bildschirmseite (`A3Seite` mit `overflow: hidden`) — eine gedruckte
 * Seite kann nicht wachsen.
 *
 * ═══ LEERE STELLEN SAGEN, DASS SIE LEER SIND ══════════════════════════════
 * Ein Handbuch ohne Baustellenordnung darf gedruckt werden. Es darf aber nicht
 * so aussehen, als gaebe es keine Regeln — es gibt sie nur noch nicht. Die
 * Fehlmeldungen kommen wortgleich aus `kapitelBefunde()`, damit Bildschirm und
 * Papier dieselbe Auskunft geben.
 *
 * ═══ KEIN BRIEFKOPF ═══════════════════════════════════════════════════════
 * `letterhead.ts` ist auf A4-Geschaeftsbriefe gebaut (Anschriftfeld,
 * Falzmarken, Fusszeile mit Bankverbindung). Ein Handbuch ist ein Werk und
 * kein Anschreiben; sein Kopf traegt Projekt, Kapitel und Seitenzahl.
 */

// ═══════════════════════════════════════════════════════════════════════════
// FORMAT UND GEOMETRIE
// ═══════════════════════════════════════════════════════════════════════════

/** A3 quer in Millimetern. */
export const HANDBUCH_MASSE: [number, number] = [420, 297];

export const HANDBUCH_TITEL = 'SiGe-Handbuch';

const GEO = {
    breite: 420,
    hoehe: 297,
    rand: 14,
    /** Oberkante des Inhalts — darueber Kopfzeile und Trennlinie. */
    inhaltOben: 32,
    /** Unterkante des Inhalts — darunter nur noch die Fusszeile. */
    inhaltUnten: 278,
    spaltenAbstand: 10,
};

const SCHRIFT = {
    kopf: 9,
    kopfTitel: 13,
    kapitel: 15,
    ueberschrift: 10,
    block: 8.5,
    klein: 7.2,
    tabelle: 7.2,
    matrix: 6.4,
    fuss: 7,
};

const ZEILE = {
    block: 4.0,
    klein: 3.4,
    tabelle: 3.6,
};

const FARBE = {
    text: [26, 26, 26] as [number, number, number],
    leise: [90, 96, 105] as [number, number, number],
    linie: [153, 153, 153] as [number, number, number],
    linieStark: [51, 51, 51] as [number, number, number],
    band: [232, 232, 232] as [number, number, number],
    rot: [185, 28, 28] as [number, number, number],
    orange: [217, 119, 6] as [number, number, number],
    gruen: [22, 163, 74] as [number, number, number],
    grau: [244, 245, 246] as [number, number, number],
};

const AMPEL_FARBE: Record<string, [number, number, number]> = {
    rot: [250, 205, 205],
    orange: [253, 230, 180],
    grau: [246, 247, 248],
};

/**
 * Hoechstzahl der Gewerke, die als Kreuzmatrix auf die A3-Tafel passen.
 *
 * Rechnung: 392 mm Satzbreite minus 60 mm Namensspalte = 332 mm fuer n
 * Spalten. Unter 16 mm ist die Zahl in der Zelle bei 6,4 pt nicht mehr sicher
 * zu treffen — 332 / 16 ≈ 20. Bei mehr Gewerken wird die Matrix WEGGELASSEN
 * und der Verzicht vermerkt: eine unlesbar gequetschte Matrix waere schlimmer
 * als keine, denn sie saehe aus wie eine Aussage.
 */
export const MATRIX_MAX_GEWERKE = 20;

/** Kantenlaenge des QR-Codes der Online-Einweisung auf dem Deckblatt. */
const EINWEISUNG_QR_MM = 30;
/** Innenrand des Einweisungsblocks — um den QR-Code und um den Text. */
const EINWEISUNG_QR_RAND = 3;

// ═══════════════════════════════════════════════════════════════════════════
// EINGABE
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die Projektangaben, so weit das Handbuch sie braucht.
 *
 * Bewusst fertige Zeichenketten und nicht `ReportMetadata`: der Generator soll
 * ohne den Projektzweig testbar bleiben, und `src/types.ts` ist der
 * Bug-Magnet dieses Repositoriums (20 Fehlerbehebungen in 90 Tagen). Die
 * Abbildung macht `components/handbuch/handbuchIntegration.ts` — dort und nur
 * dort steht, welches Feld aus `metadata` woher kommt.
 */
export interface HandbuchProjektAngaben {
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    bauherrName?: string;
    bauherrAdresse?: string;
    bauherrKontakt?: string;
    bauherrTelefon?: string;
    koordinatorPerson?: string;
    koordinatorFirma?: string;
    koordinatorTelefon?: string;
    bauleitungFirma?: string;
    bauleitungPerson?: string;
    bauleitungTelefon?: string;
    auftragnehmer?: string;
    /** ISO-Tagesdaten aus `globalData.startDate` / `endDate`. */
    bauzeitVon?: string;
    bauzeitBis?: string;
    planNr?: string;
    planVersion?: string;
    /** Anzahl der hinterlegten Lageplaene (`metadata.plans`). */
    anzahlLageplaene?: number;
    /** Der Verteiler, auf Rolle und Firma eingedampft. */
    beteiligte?: HandbuchBeteiligter[];
}

export interface HandbuchBeteiligter {
    rolle?: string;
    name?: string;
    firma?: string;
    telefon?: string;
    /** Gewerk aus `Contact.tradeType` — verbindet den Verteiler mit der Seite. */
    gewerk?: string;
}

/** Eine Planfassung fuer die Aenderungshistorie. */
export interface HandbuchPlanfassung {
    plan_nr?: string | null;
    version_label?: string | null;
    /** Zeitstempel; es wird ausschliesslich der Datumsteil gelesen. */
    created_at?: string | null;
}

export interface HandbuchPdfDaten {
    lage: HandbuchLage;
    projekt: HandbuchProjektAngaben;
    /** Gewerke der Planfassung — Ersatzquelle fuer Zeitraeume und Taetigkeiten. */
    planGewerke?: GewerkAusPlan[];
    /** Arbeitsschritte der Planfassung. */
    schritte?: Arbeitsschritt[];
    /** Gefaehrdungen je Arbeitsschritt-id. */
    gefaehrdungen?: Record<string, Gefaehrdung[]>;
    /** Schutzmassnahmen je Gefaehrdungs-id. */
    massnahmen?: Record<string, Schutzmassnahme[]>;
    /** Inhalt von `sige_bloecke[typ='notrufplan']`. Wird NUR gelesen. */
    notrufplan?: NotrufplanInhalt | null;
    /** Bewertungen aus `koordinierung_paare`. */
    paare?: KoordinierungPaar[];
    /** Projektablage (`fetchProjektDokumente`). */
    dokumente?: ProjektDokument[];
    /** Fassungen des SiGe-Plans fuer die Historie. */
    planFassungen?: HandbuchPlanfassung[];
    /**
     * `true`, wenn die Planfassungen gar nicht geladen wurden.
     *
     * Der Unterschied zaehlt: „keine Planfassung vorhanden" und „nicht
     * nachgesehen" sehen auf dem Blatt gleich aus und meinen Gegenteiliges.
     */
    planUngeladen?: boolean;
    /** ISO-Tagesdatum fuer Fristen. Wird hereingereicht, nie hier gebildet. */
    heuteIso: string;
    /**
     * QR-Code der Online-Einweisung (juengste Einweisung mit aktivem Link).
     *
     * `null`/fehlend = kein aktiver Link: dann wird auf dem Deckblatt NICHTS
     * gezeichnet, kein Platzhalter. Kein eigenes Kapitel — `seitenPlan` und
     * die Vollstaendigkeitspruefung bleiben davon unberuehrt.
     */
    einweisungQr?: EinweisungQrQuelle | null;
}

export interface HandbuchPdfOptionen {
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten. Nur fuer Tests —
     * dasselbe Muster wie in den uebrigen Generatoren dieses Ordners.
     */
    __docFactory?: () => jsPDF;
}

// ═══════════════════════════════════════════════════════════════════════════
// KLEINKRAM
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Ein Wert, wie er aufs Blatt darf: einzeilig und in CP1252 setzbar.
 *
 * Die Kodierung wird HIER schon geradegezogen und nicht erst in
 * `sichereTextausgabe`, weil `splitTextToSize` sonst eine andere Zeichenkette
 * ausmisst als die, die spaeter gesetzt wird — der Umbruch stuende dann an der
 * falschen Stelle. Betroffen ist ausschliesslich FREIER Text (Projektnamen,
 * Bemerkungen, Gewerkebezeichnungen); die Textbausteine dieses Generators
 * haelt der Waechtertest in `winAnsi.test.ts` sauber.
 */
const text = (v?: string | null): string =>
    winAnsiSicher((v == null ? '' : String(v)).replace(/\s*\n\s*/g, ' ').replace(/\s+/g, ' ').trim());

/**
 * ISO-Tagesdatum als deutsches Datum, rein zeichenweise.
 *
 * Kein `new Date(...)` und kein `toISOString()`: deren Ergebnis haengt an der
 * Zeitzone der Laufzeit, und ein Bauzeitbeginn, der im gedruckten Handbuch
 * einen Tag danebenliegt, ist schlimmer als gar keiner. Gleiche Ueberlegung
 * wie in `sige-plan/zeitraum.ts` und `koordinierung/ueberschneidung.ts`.
 */
const tag = (iso?: string | null): string =>
    typeof iso === 'string' && iso.length >= 10
        ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`
        : '—';

const zeitraumText = (von?: string | null, bis?: string | null): string =>
    von || bis ? `${tag(von)} – ${tag(bis)}` : 'kein Zeitraum hinterlegt';

/* Der Zeitraum einer Gewerkeseite stand hier einmal nachgebaut (`gewerk.
 * zeitraum_von ?? plan?.zeitraum_von`) — die fuenfte Abschrift derselben
 * Regel. Der Nachbau kannte den Freigabestand des Handbuchs nicht und las
 * deshalb auch fuer eine FREIGEGEBENE Fassung weiter den laufenden Plan:
 * derselbe Druck derselben Fassung zeigte nach einer Planverschiebung andere
 * Daten. Gerechnet wird jetzt mit `wirksamerZeitraumDerFassung` aus
 * `handbuchLogic.ts` — derselben Funktion, die auch der Bildschirm benutzt.
 * Zwei Rechnungen waeren zwei Wahrheiten, und genau daran ist dieses Modul in
 * der Pruefung schon einmal gescheitert. Gebunden wird sie in
 * `renderHandbuch`, weil sie den Handbuchsatz braucht (`zeitraumDerFassung`). */

/* Die Bruecke zwischen den zwei Schluesselraeumen (`gewerk_norm` des Handbuchs
 * gegen `gewerkSchluessel` der Koordinierung) stand hier einmal nachgebaut —
 * und zwar ueber `gewerk_label`. Das Label ist von Hand aenderbar: wer die
 * Anzeige von „Elektro" auf den Firmennamen umschreibt, verlor ueber diesen
 * Nachbau jede gespeicherte Bewertung, waehrend der Bildschirm sie weiter
 * zeigte. Gedruckt stand dann „nicht bewertet". Benutzt wird jetzt die eine
 * geprüfte Bruecke aus `handbuchLogic.ts` (`koordinierungsSchluessel`, ueber
 * `gewerk_norm` mit dem Label als Rueckfallebene) — zwei Bruecken waeren zwei
 * Wahrheiten. */

/** Uebersetzt eine Gewerkeseite in die Form, die `baueZeitachse` erwartet.
 *  Der Schluessel bleibt `gewerk_norm` des Handbuchs: im Terminplan soll jede
 *  Gewerkeseite ihren eigenen Balken haben — „Elektro" und „Elektro NU 2"
 *  sind zwei Firmen mit zwei Bauzeiten. */
const alsZeitachsenGewerk = (
    gewerk: HandbuchGewerk,
    zeitraum: { von: string | null; bis: string | null },
): KoordinierungsGewerk => ({
    norm: gewerk.gewerk_norm,
    label: gewerk.gewerk_label,
    von: zeitraum.von,
    bis: zeitraum.bis,
    treffer: UNBEKANNT,
    zugeordnet: false,
    vonHandZugeordnet: false,
    schreibweisen: [gewerk.gewerk_label],
    schrittIds: [],
    anzahlSchritte: 0,
    schritteOhneDatum: 0,
    bereiche: [],
});

// ═══════════════════════════════════════════════════════════════════════════
// DER SATZ
// ═══════════════════════════════════════════════════════════════════════════

/** Ein Schreibbereich — eine Spalte der Tafel. */
interface Bereich {
    x: number;
    breite: number;
    y: number;
    unten: number;
    /**
     * In dieser Spalte steht bereits ein Platzmangel-Vermerk.
     *
     * Ein voller Bereich loest oft MEHRERE Abbrueche hintereinander aus
     * (Ueberschrift passt nicht, danach die Tabelle darunter auch nicht).
     * Jeder von ihnen schriebe seinen Vermerk an dieselbe letzte Zeile — der
     * zweite ueberdruckte den ersten, und heraus kaeme ein unlesbarer Klumpen
     * statt einer Auskunft. Es wird deshalb genau EINER gesetzt: der erste,
     * denn er benennt die Stelle, an der das Blatt zu Ende war.
     */
    vermerkt?: boolean;
}

const renderHandbuch = (doc: jsPDF, daten: HandbuchPdfDaten): void => {
    const lage = daten.lage || { handbuch: null, gewerke: [], nachweise: [] };
    const hb = lage.handbuch;
    const gewerke = [...(lage.gewerke || [])].sort((a, b) => a.sortierung - b.sortierung);
    const nachweise = lage.nachweise || [];
    const projekt = daten.projekt || {};
    const planGewerke = daten.planGewerke || [];
    const ausPlan = new Map<string, GewerkAusPlan>(planGewerke.map(g => [g.gewerk_norm, g]));

    /**
     * Der Zeitraum einer Gewerkeseite — fuer DIESE Fassung.
     *
     * Der Handbuchsatz gehoert in die Rechnung, weil ein freigegebenes
     * Handbuch nicht mehr auf den laufenden Plan zurueckgreifen darf; ein
     * Entwurf dagegen soll die Planverschiebung zeigen. Die Unterscheidung
     * trifft `wirksamerZeitraumDerFassung` und sonst nichts — hier steht nur
     * die Bindung an `ausPlan` und `hb`, damit die vier Kapitel des
     * Generators nicht viermal dieselben drei Argumente aufzaehlen.
     */
    const zeitraumDerFassung = (
        g: HandbuchGewerk,
    ): { von: string | null; bis: string | null } => wirksamerZeitraumDerFassung(g, ausPlan, hb);
    const paare = (daten.paare || []).filter(Boolean);
    const heute = alsTag(daten.heuteIso) || daten.heuteIso;

    const seiten = seitenPlan(lage);
    const gesamt = seiten.length;

    // Die Fehlmeldungen der Kapitel kommen wortgleich aus der geprueften
    // Logik. Sie hier neu zu formulieren hiesse, dass Bildschirm und Papier
    // verschiedene Auskuenfte geben — und der Anwender kaeme nie dahinter,
    // welche stimmt.
    const befunde = new Map<KapitelId, string[]>(
        kapitelBefunde(lage, heute, planGewerke).map(b => [
            b.kapitel,
            [...b.fehlend, ...(b.hinweis ? [b.hinweis] : [])],
        ]),
    );

    const links = GEO.rand;
    const rechts = GEO.breite - GEO.rand;
    const satzBreite = rechts - links;

    const setz = (groesse: number, stil: 'normal' | 'bold' | 'italic', farbe = FARBE.text) => {
        doc.setFont('helvetica', stil);
        doc.setFontSize(groesse);
        doc.setTextColor(farbe[0], farbe[1], farbe[2]);
    };

    const strich = (x1: number, y1: number, x2: number, y2: number, staerke = 0.2, farbe = FARBE.linie) => {
        doc.setDrawColor(farbe[0], farbe[1], farbe[2]);
        doc.setLineWidth(staerke);
        doc.line(x1, y1, x2, y2);
    };

    // ── Kopf und Fuss ───────────────────────────────────────────────────

    const kopfZeichnen = (eintrag: SeitenEintrag) => {
        setz(SCHRIFT.kopf, 'normal', FARBE.leise);
        doc.text(text(projekt.projektName) || 'Projekt ohne Namen', links, 18);
        const rechteAngaben = [
            text(projekt.projektNummer) && `Projekt-Nr. ${text(projekt.projektNummer)}`,
            `Seite ${eintrag.nr} von ${gesamt}`,
        ].filter(Boolean).join('   ·   ');
        doc.text(rechteAngaben, rechts, 18, { align: 'right' });

        setz(SCHRIFT.kopfTitel, 'bold', FARBE.text);
        doc.text(`${HANDBUCH_TITEL} — ${eintrag.titel}`, links, 25);
        strich(links, 27.5, rechts, 27.5, 0.5, FARBE.linieStark);
    };

    const fussZeichnen = () => {
        strich(links, 283, rechts, 283, 0.2, FARBE.linie);
        setz(SCHRIFT.fuss, 'normal', FARBE.leise);
        const linksText = [
            hb ? `Stand ${text(hb.stand) || '—'}` : 'Noch kein Handbuch angelegt',
            hb && text(hb.version_label) ? `Fassung ${text(hb.version_label)}` : '',
            hb ? `Katalogfassung ${text(hb.katalog_version) || '—'}` : '',
        ].filter(Boolean).join('   ·   ');
        doc.text(linksText, links, 288);
        // Der Freigabevermerk gehoert auf JEDE Seite: ein einzelnes Blatt aus
        // dem Handbuch wandert erfahrungsgemaess allein durch die Baustelle.
        const status = hb?.status === 'freigegeben'
            ? `Freigegeben${text(hb.freigegeben_von_name) ? ` durch ${text(hb.freigegeben_von_name)}` : ''}`
                + `${hb.freigegeben_am ? ` am ${tag(hb.freigegeben_am)}` : ''}`
            : 'ENTWURF — noch nicht freigegeben';
        doc.text(status, rechts, 288, { align: 'right' });
    };

    // ── Schreibwerkzeug ─────────────────────────────────────────────────

    const neuerBereich = (x: number, breite: number): Bereich =>
        ({ x, breite, y: GEO.inhaltOben, unten: GEO.inhaltUnten });

    /** Zwei gleich breite Spalten der Tafel. */
    const zweiSpalten = (): [Bereich, Bereich] => {
        const b = (satzBreite - GEO.spaltenAbstand) / 2;
        return [neuerBereich(links, b), neuerBereich(links + b + GEO.spaltenAbstand, b)];
    };

    /** Passt `hoehe` noch in den Bereich? */
    const passt = (b: Bereich, hoehe: number): boolean => b.y + hoehe <= b.unten;

    /**
     * Der Vermerk, dass an dieser Stelle das Blatt zu Ende war.
     *
     * Wird gebraucht, weil ein Kapitel genau eine Seite hat (siehe Dateikopf).
     * Ohne ihn saehe ein gekuerztes Blatt vollstaendig aus — und eine Liste
     * von Schutzmassnahmen, die vollstaendig aussieht und es nicht ist, ist die
     * gefaehrlichste Zeile in diesem Dokument. Er wird notfalls IN die letzte
     * Zeile geklemmt (Muster: `vermerk()` im Aushang) und nur einmal je Spalte
     * gesetzt.
     */
    const platzVermerk = (b: Bereich, satz: string) => {
        if (b.vermerkt) return;
        b.vermerkt = true;
        setz(SCHRIFT.klein, 'italic', FARBE.rot);
        doc.text(satz, b.x, Math.min(b.y + ZEILE.klein, b.unten));
        b.y = b.unten;
    };

    /** Der Abschnittsvermerk fuer eine abgeschnittene Liste. */
    const abschnittVermerk = (b: Bereich, rest: number) => {
        if (rest <= 0) return;
        platzVermerk(b, `… und ${rest} weitere — vollständig im Modul „SiGe-Handbuch"`);
    };

    const bandUeberschrift = (b: Bereich, titel: string) => {
        if (!passt(b, 9)) {
            // Frueher fiel die Ueberschrift hier ersatzlos weg — und mit ihr
            // die Tabelle darunter, die ohne ihren Kopf ebenfalls abbrach. Bei
            // 40 Beteiligten verschwand so die gesamte Gewerketabelle, ohne
            // dass das Blatt ein Wort darueber verlor.
            platzVermerk(b, `„${titel}" hat auf diesem Blatt keinen Platz mehr — vollständig im Modul „SiGe-Handbuch"`);
            return;
        }
        doc.setFillColor(FARBE.band[0], FARBE.band[1], FARBE.band[2]);
        doc.rect(b.x, b.y, b.breite, 5.6, 'F');
        setz(SCHRIFT.ueberschrift, 'bold', FARBE.text);
        doc.text(titel.toUpperCase(), b.x + 1.6, b.y + 4);
        b.y += 8.4;
    };

    const absatz = (
        b: Bereich,
        inhalt: string,
        stil: 'normal' | 'bold' | 'italic' = 'normal',
        groesse = SCHRIFT.block,
        farbe = FARBE.text,
    ) => {
        const roh = text(inhalt);
        if (!roh) return;
        setz(groesse, stil, farbe);
        const hoehe = groesse === SCHRIFT.klein ? ZEILE.klein : ZEILE.block;
        (doc.splitTextToSize(roh, b.breite) as string[]).forEach(z => {
            if (!passt(b, hoehe)) return;
            b.y += hoehe;
            doc.text(z, b.x, b.y);
        });
        b.y += 1.4;
    };

    /** Beschriftetes Feld: Bezeichnung mager, Wert fett — eine Zeile je Feld,
     *  solange der Wert passt. */
    const feld = (b: Bereich, bezeichnung: string, wert?: string | null) => {
        const labelBreite = Math.min(38, b.breite * 0.42);
        setz(SCHRIFT.klein, 'normal', FARBE.leise);
        const w = text(wert) || '—';
        setz(SCHRIFT.block, 'bold', FARBE.text);
        const zeilen = doc.splitTextToSize(w, b.breite - labelBreite) as string[];
        if (!passt(b, ZEILE.block * zeilen.length)) return;
        const start = b.y + ZEILE.block;
        setz(SCHRIFT.klein, 'normal', FARBE.leise);
        doc.text(bezeichnung, b.x, start);
        setz(SCHRIFT.block, 'bold', FARBE.text);
        zeilen.forEach((z, i) => {
            doc.text(z, b.x + labelBreite, start + i * ZEILE.block);
        });
        b.y = start + (zeilen.length - 1) * ZEILE.block + 1.2;
    };

    /**
     * Der QR-Code der Online-Einweisung — als Kasten mit fester Hoehe an
     * einer festen Oberkante, nicht im Fluss der Spalte. Der Aufrufer
     * reserviert den Platz vorher (`b.unten` absenken), damit der
     * Seitenspiegel sich selbst klemmt, statt unter den Code zu laufen.
     */
    const einweisungBlock = (b: Bereich, qr: EinweisungQrQuelle, oben: number, hoehe: number) => {
        const rand = EINWEISUNG_QR_RAND;
        doc.setFillColor(FARBE.grau[0], FARBE.grau[1], FARBE.grau[2]);
        doc.setDrawColor(FARBE.linie[0], FARBE.linie[1], FARBE.linie[2]);
        doc.setLineWidth(0.2);
        doc.rect(b.x, oben, b.breite, hoehe, 'FD');
        try {
            doc.addImage(qr.dataUrl, 'PNG', b.x + rand, oben + rand, EINWEISUNG_QR_MM, EINWEISUNG_QR_MM);
        } catch (e) {
            console.warn('QR-Code der Einweisung konnte nicht eingebettet werden', e);
        }
        const tx = b.x + rand + EINWEISUNG_QR_MM + rand;
        const tw = b.x + b.breite - rand - tx;
        let y = oben + rand + 4;
        setz(SCHRIFT.ueberschrift, 'bold', FARBE.text);
        doc.text('Einweisung in den SiGe-Plan per QR', tx, y);
        y += ZEILE.block + 1;
        setz(SCHRIFT.block, 'normal', FARBE.text);
        const satz = doc.splitTextToSize(
            `QR-Code scannen, Einweisung lesen, Name eintragen und unterschreiben. Einweisung Nr. ${qr.nummer}.`,
            tw,
        ) as string[];
        satz.slice(0, 3).forEach(z => { doc.text(z, tx, y); y += ZEILE.block; });
        y += 1;
        // Der Link klein darunter — fuer alle, die den Code nicht scannen koennen.
        setz(SCHRIFT.klein, 'normal', FARBE.leise);
        const link = doc.splitTextToSize(text(qr.url), tw) as string[];
        link.slice(0, 2).forEach(z => { doc.text(z, tx, y); y += ZEILE.klein; });
    };

    /** Waagerechter Abstand einer Hierarchiestufe im Organigramm-Baum. */
    const BAUM_EINZUG = 6;

    /**
     * Der Organigramm-Baum mit GEZEICHNETEN Verbindungslinien.
     *
     * ═══ WARUM NICHT MEHR ALS TEXTBAUM ════════════════════════════════════
     * Hier stand der Baum einmal als Zeichenkette aus Rahmenzeichen
     * (`'  └─ Bauherr'`). jsPDF setzt mit den PDF-Standardschriften, und die
     * koennen nur CP1252. Ein Rahmenzeichen kippt die GANZE Zeile in UTF-16;
     * auf dem gedruckten Blatt stand daraufhin '%%' statt der Winkel und
     * 'B a u h e r r' statt des Namens (Herleitung in `winAnsi.ts`). Ein
     * ASCII-Baum aus '+' und '|' waere zwar setzbar, saehe aber auf einem
     * Blatt fuer Bauherren und Unternehmer nach Fernschreiber aus.
     *
     * Gezeichnete Linien haben das Kodierungsproblem nicht und sind ausserdem
     * dieselbe Bildsprache wie am Bildschirm: `kapitel/Organigramm.tsx`
     * zeichnet seine Senkrechten und Waagerechten ebenfalls.
     */
    const strukturBaum = (b: Bereich, knoten: OrgStrukturKnoten[]) => {
        // Ab welchem y die Senkrechte einer Stufe weiterlaeuft. Beim ersten
        // Kind ist das die Unterkante des Elternknotens, bei jedem weiteren
        // Geschwister der Abzweig davor — so bleibt der Stamm durchgezogen
        // statt in Stummel zu zerfallen.
        const stammAb: number[] = [];
        for (let i = 0; i < knoten.length; i++) {
            const k = knoten[i];
            if (!passt(b, ZEILE.block)) {
                abschnittVermerk(b, knoten.length - i);
                return;
            }
            b.y += ZEILE.block;
            const textX = b.x + k.tiefe * BAUM_EINZUG;
            // Der Abzweig sitzt auf Höhe der Buchstabenmitte, nicht auf der
            // Grundlinie — sonst haengt die Waagerechte unter dem Wort.
            const abzweigY = b.y - 1.1;
            if (k.tiefe > 0) {
                const stammX = b.x + (k.tiefe - 1) * BAUM_EINZUG + 1.4;
                strich(stammX, stammAb[k.tiefe] ?? abzweigY, stammX, abzweigY, 0.2, FARBE.linie);
                strich(stammX, abzweigY, textX - 1.0, abzweigY, 0.2, FARBE.linie);
            }
            setz(SCHRIFT.block, k.tiefe === 0 ? 'bold' : 'normal', FARBE.text);
            doc.text(k.label, textX, b.y);

            stammAb[k.tiefe] = abzweigY;
            // Tiefere Staende gehoeren ab hier zu einem ANDEREN Elternknoten;
            // blieben sie stehen, zoege der naechste Zweig seine Senkrechte
            // aus einem fremden Ast heraus.
            stammAb.length = k.tiefe + 1;
            stammAb[k.tiefe + 1] = b.y + 0.8;
        }
    };

    /** Aufzaehlung mit Abschnittsvermerk. */
    const punkte = (b: Bereich, eintraege: string[], zeichen = '•') => {
        const liste = (eintraege || []).map(text).filter(Boolean);
        for (let i = 0; i < liste.length; i++) {
            const zeilen = doc.splitTextToSize(liste[i], b.breite - 4.5) as string[];
            // Der Vermerk selbst braucht auch eine Zeile — deshalb wird gegen
            // zwei Zeilen geprueft, sobald noch etwas folgt.
            const noetig = zeilen.length * ZEILE.block + (i < liste.length - 1 ? ZEILE.klein : 0);
            if (!passt(b, noetig)) {
                abschnittVermerk(b, liste.length - i);
                return;
            }
            setz(SCHRIFT.block, 'normal', FARBE.text);
            doc.text(zeichen === '#' ? `${i + 1}.` : zeichen, b.x, b.y + ZEILE.block);
            zeilen.forEach(z => {
                b.y += ZEILE.block;
                doc.text(z, b.x + 4.5, b.y);
            });
            b.y += 0.8;
        }
    };

    /**
     * Die Fehlmeldungen eines Kapitels — wortgleich aus `kapitelBefunde`.
     *
     * ═══ DIESER KASTEN DARF NIE DEM PLATZMANGEL ZUM OPFER FALLEN ═══════════
     * Er sagt, was auf diesem Blatt noch fehlt. Er brach frueher stumm ab,
     * sobald die Spalte voll war — ausgerechnet auf einer vollgeschriebenen
     * Baustellenordnungsseite verschwand also der Hinweis, dass Arbeitszeiten
     * und Sammelplatz fehlen, und das Blatt sah vollstaendig aus. Er wird
     * deshalb wie `vermerk()` im Aushang gegen die Unterkante GEKLEMMT statt
     * weggelassen: lieber rutscht er in den letzten Inhalt hinein, als dass er
     * fehlt.
     */
    const offeneStellen = (b: Bereich, kapitel: KapitelId) => {
        const saetze = befunde.get(kapitel) || [];
        if (!saetze.length) return;

        // Erst messen, dann klemmen: die Saetze brechen um, und eine
        // Kastenhoehe „ein Satz = eine Zeile" schnitte den letzten ab.
        setz(SCHRIFT.klein, 'italic', FARBE.leise);
        const zeilen = saetze.flatMap(s => doc.splitTextToSize(`· ${s}`, b.breite - 3.2) as string[]);
        const gewuenscht = 4 + (zeilen.length + 1) * ZEILE.klein + 3;
        const hoehe = Math.min(gewuenscht, b.unten - GEO.inhaltOben);
        b.y = Math.max(GEO.inhaltOben, Math.min(b.y + 2, b.unten - hoehe));

        doc.setFillColor(FARBE.grau[0], FARBE.grau[1], FARBE.grau[2]);
        doc.rect(b.x, b.y, b.breite, hoehe, 'F');
        b.y += 1;
        setz(SCHRIFT.klein, 'bold', FARBE.rot);
        b.y += ZEILE.klein;
        doc.text('Was hier noch fehlt', b.x + 1.6, b.y);
        setz(SCHRIFT.klein, 'italic', FARBE.leise);
        zeilen.forEach(z => {
            if (!passt(b, ZEILE.klein)) return;
            b.y += ZEILE.klein;
            doc.text(z, b.x + 1.6, b.y);
        });
        b.y += 3;
    };

    /**
     * Tabelle mit fester Spaltenbreite.
     *
     * Bewusst von Hand gesetzt und nicht ueber `jspdf-autotable`: autoTable
     * legt bei Ueberlauf selbstaendig eine neue Seite an, und genau das darf
     * hier nicht passieren — die Seitenzahl steht vorher fest. Der Ueberlauf
     * wird abgeschnitten und vermerkt.
     */
    const tabelle = (
        b: Bereich,
        kopf: string[],
        anteile: number[],
        zeilen: string[][],
        leerText: string,
    ) => {
        const summe = anteile.reduce((a, v) => a + v, 0) || 1;
        const breiten = anteile.map(a => (a / summe) * b.breite);
        const x = (i: number) => b.x + breiten.slice(0, i).reduce((a, v) => a + v, 0);

        if (!passt(b, ZEILE.tabelle * 2)) {
            // Nicht einmal der Tabellenkopf passt noch. Frueher fiel die
            // Tabelle hier stumm weg — samt ihrer Zeilen. Was verlorengeht,
            // wird jetzt gezaehlt.
            abschnittVermerk(b, zeilen.length);
            return;
        }
        setz(SCHRIFT.tabelle, 'bold', FARBE.text);
        b.y += ZEILE.tabelle;
        kopf.forEach((t, i) => doc.text(doc.splitTextToSize(t, breiten[i] - 1.4)[0] as string, x(i) + 0.7, b.y));
        b.y += 1.2;
        strich(b.x, b.y, b.x + b.breite, b.y, 0.3, FARBE.linieStark);

        if (!zeilen.length) {
            absatz(b, leerText, 'italic', SCHRIFT.klein, FARBE.leise);
            return;
        }

        for (let i = 0; i < zeilen.length; i++) {
            const umbrochen = zeilen[i].map((z, s) => doc.splitTextToSize(text(z) || '—', breiten[s] - 1.4) as string[]);
            const zahl = umbrochen.reduce((a, z) => Math.max(a, z.length), 1);
            const hoehe = zahl * ZEILE.tabelle + 0.8;
            if (!passt(b, hoehe + (i < zeilen.length - 1 ? ZEILE.klein : 0))) {
                abschnittVermerk(b, zeilen.length - i);
                return;
            }
            setz(SCHRIFT.tabelle, 'normal', FARBE.text);
            umbrochen.forEach((spalte, s) => {
                spalte.forEach((z, j) => doc.text(z, x(s) + 0.7, b.y + ZEILE.tabelle * (j + 1)));
            });
            b.y += hoehe;
            strich(b.x, b.y - 0.4, b.x + b.breite, b.y - 0.4, 0.1, FARBE.linie);
        }
    };

    // ═══════════════════════════════════════════════════════════════════
    // KAPITEL
    // ═══════════════════════════════════════════════════════════════════

    const kapitelDeckblatt = () => {
        const [l, r] = zweiSpalten();

        bandUeberschrift(l, 'Bauvorhaben');
        feld(l, 'Projekt', projekt.projektName);
        feld(l, 'Projekt-Nr.', projekt.projektNummer);
        feld(l, 'Anschrift', projekt.projektAdresse);
        feld(l, 'Bauzeit', zeitraumText(projekt.bauzeitVon, projekt.bauzeitBis));
        l.y += 3;

        bandUeberschrift(l, 'Bauherr');
        feld(l, 'Name', projekt.bauherrName);
        feld(l, 'Anschrift', projekt.bauherrAdresse);
        feld(l, 'Ansprechpartner', projekt.bauherrKontakt);
        feld(l, 'Telefon', projekt.bauherrTelefon);
        l.y += 3;

        bandUeberschrift(l, 'Sicherheits- und Gesundheitsschutzkoordinator');
        feld(l, 'Firma', projekt.koordinatorFirma);
        feld(l, 'Koordinator', projekt.koordinatorPerson);
        feld(l, 'Telefon', projekt.koordinatorTelefon);

        bandUeberschrift(r, 'Fassung dieses Handbuchs');
        feld(r, 'Vergabemodell', orgModellLabel(hb?.org_modell));
        feld(r, 'Stand', hb ? text(hb.stand) : null);
        feld(r, 'Fassung', hb ? text(hb.version_label) : null);
        feld(r, 'Gültig von', hb?.gueltig_von ? tag(hb.gueltig_von) : null);
        feld(r, 'Gültig bis', hb?.gueltig_bis ? tag(hb.gueltig_bis) : null);
        feld(r, 'Katalogfassung', hb ? text(hb.katalog_version) : null);
        feld(r, 'SiGe-Plan', [text(projekt.planNr), text(projekt.planVersion)].filter(Boolean).join(' · '));
        feld(r, 'Gewerkeseiten', String(gewerke.length));
        const sperre = sperrHinweis(hb);
        if (sperre) absatz(r, sperre, 'italic', SCHRIFT.klein, FARBE.leise);
        r.y += 3;

        bandUeberschrift(r, 'Inhalt');
        // Der QR-Code der Online-Einweisung steht unten rechts. Sein Platz
        // wird VOR dem Seitenspiegel reserviert: die Tabelle klemmt sich dann
        // selbst (mit Vermerk), statt unter den Code zu laufen. Ohne aktiven
        // Link wird weder reserviert noch gezeichnet.
        const qr = daten.einweisungQr;
        const qrBlockH = qr ? EINWEISUNG_QR_MM + 2 * EINWEISUNG_QR_RAND : 0;
        if (qr) r.unten -= qrBlockH + 3;
        // Der Seitenspiegel steht auf dem Deckblatt, weil das Handbuch
        // ausgedruckt und geheftet wird: ohne Inhaltsverzeichnis findet
        // niemand die Seite seines Gewerks.
        tabelle(
            r,
            ['Seite', 'Kapitel'],
            [1, 7],
            seiten.map(s => [String(s.nr), s.titel]),
            'Der Seitenspiegel ist leer.',
        );
        if (qr) einweisungBlock(r, qr, GEO.inhaltUnten - qrBlockH, qrBlockH);

        offeneStellen(l, 'deckblatt');
    };

    const kapitelOrganigramm = () => {
        const [l, r] = zweiSpalten();
        const konfig = hb?.org_modell ? ORG_MODELLE[hb.org_modell] : null;

        bandUeberschrift(l, 'Vergabemodell');
        if (!konfig) {
            absatz(
                l,
                'Das Vergabemodell ist noch nicht bestimmt. Ohne es lässt sich kein Organigramm '
                + 'zeichnen — ein geratenes Modell zeichnet ein falsches Organigramm, und ein '
                + 'falsches Organigramm sieht richtig aus.',
                'italic', SCHRIFT.block, FARBE.leise,
            );
        } else {
            absatz(l, konfig.label, 'bold', SCHRIFT.ueberschrift);
            absatz(l, konfig.beschreibung, 'italic', SCHRIFT.klein, FARBE.leise);
            absatz(l, konfig.erklaerung, 'normal', SCHRIFT.block);
            l.y += 2;
            bandUeberschrift(l, 'Struktur');
            // Die Kaestchenzeichnung der Quell-App wird als eingerueckter Baum
            // mit gezeichneten Linien gesetzt. Auf Papier ist er genauso
            // eindeutig und er bricht nicht.
            strukturBaum(l, konfig.struktur);
            l.y += 2;
            absatz(
                l,
                `Die Gewerke hängen unter: ${konfig.gewerkeUnter.toUpperCase()}.`
                + (konfig.hatEigenePlanung ? ' Der Auftragnehmer plant selbst.' : ''),
                'italic', SCHRIFT.klein, FARBE.leise,
            );
        }

        bandUeberschrift(r, 'Beteiligte');
        const rollen = konfig ? konfig.beteiligteRollen.map(x => ROLLE_LABEL[x]).join(' · ') : '';
        if (rollen) absatz(r, `Das Modell sieht vor: ${rollen}`, 'italic', SCHRIFT.klein, FARBE.leise);
        const beteiligte = (projekt.beteiligte || []).filter(p => text(p.name) || text(p.firma));
        tabelle(
            r,
            ['Rolle / Gewerk', 'Firma', 'Name', 'Telefon'],
            [2, 3, 3, 2],
            [
                ...(text(projekt.bauleitungFirma) || text(projekt.bauleitungPerson)
                    ? [['Bauleitung vor Ort', text(projekt.bauleitungFirma), text(projekt.bauleitungPerson), text(projekt.bauleitungTelefon)]]
                    : []),
                ...(text(projekt.auftragnehmer) ? [['Auftragnehmer', text(projekt.auftragnehmer), '', '']] : []),
                ...beteiligte.map(p => [
                    text(p.rolle) || text(p.gewerk) || '—',
                    text(p.firma),
                    text(p.name),
                    text(p.telefon),
                ]),
            ],
            'Im Verteiler des Projekts ist niemand hinterlegt.',
        );
        r.y += 2;

        bandUeberschrift(r, 'Gewerke');
        tabelle(
            r,
            ['Gewerk', 'Firma', 'Zeitraum'],
            [3, 3, 3],
            gewerke.map(g => {
                const z = zeitraumDerFassung(g);
                return [g.gewerk_label, text(g.firma), zeitraumText(z.von, z.bis)];
            }),
            'Es ist noch keine Gewerkeseite angelegt.',
        );

        offeneStellen(l, 'organigramm');
    };

    const kapitelBaustelleneinrichtung = () => {
        const [l, r] = zweiSpalten();
        const e = hb?.einrichtung;

        bandUeberschrift(l, 'Ver- und Entsorgung');
        feld(l, 'Versorgung', e?.versorgung);
        feld(l, 'Entsorgung', e?.entsorgung);
        feld(l, 'Lagerflächen', e?.lagerplatz);
        feld(l, 'Verkehrsführung', e?.verkehrsfuehrung);
        if (text(e?.bemerkung)) {
            l.y += 2;
            bandUeberschrift(l, 'Bemerkung');
            absatz(l, e?.bemerkung, 'normal', SCHRIFT.block);
        }
        l.y += 2;
        bandUeberschrift(l, 'Zugang');
        absatz(
            l,
            text(hb?.ordnung?.zugang)
            || 'Die Zugangsregelung ist in der Baustellenordnung noch nicht festgelegt.',
            text(hb?.ordnung?.zugang) ? 'normal' : 'italic',
            SCHRIFT.block,
            text(hb?.ordnung?.zugang) ? FARBE.text : FARBE.leise,
        );
        l.y += 2;
        bandUeberschrift(l, 'Lagepläne');
        absatz(
            l,
            projekt.anzahlLageplaene
                ? `${projekt.anzahlLageplaene} Lageplan/Lagepläne sind am Projekt hinterlegt und liegen dem Handbuch bei.`
                : 'Am Projekt ist kein Lageplan hinterlegt.',
            'italic', SCHRIFT.klein, FARBE.leise,
        );

        // Gemeinsame Einrichtungen werden aus den Arbeitsschritten ABGELEITET —
        // aber nur aus denen mit Hauptabschnitt „gemeinsam genutzte
        // Einrichtung: …". Hier stand einmal eine eigene Gruppierung ueber
        // JEDEN Hauptabschnitt; sie machte aus „Rohbau" eine gemeinsam
        // genutzte Einrichtung, waehrend der Bildschirm sie richtig aussortiert
        // hat. Gerechnet wird deshalb mit `gemeinsameEinrichtungen` aus
        // `handbuchLogic.ts` — derselben Funktion, die das Kapitelblatt nutzt.
        bandUeberschrift(r, 'Gemeinsam genutzte Einrichtungen');
        const einrichtungen = gemeinsameEinrichtungen(daten.schritte || []);
        tabelle(
            r,
            ['Einrichtung', 'Mitnutzer', 'Kontrolle'],
            [2, 4, 2],
            einrichtungen.map(e => [
                e.bezeichnung,
                e.ohneMitnutzer ? 'kein Gewerk zugeordnet' : e.mitnutzer.join(', '),
                e.verantwortlich.length ? e.verantwortlich.join(', ') : 'offen',
            ]),
            (daten.schritte || []).length
                ? 'Im SiGe-Plan trägt kein Arbeitsschritt den Hauptabschnitt „gemeinsam genutzte '
                  + 'Einrichtung". Verkehrswege, Lagerplätze und Gerüste sind dort einzuordnen — '
                  + 'erst dann stehen sie hier.'
                : 'Es sind keine Arbeitsschritte geladen. Die gemeinsam genutzten Einrichtungen '
                  + 'leitet dieses Kapitel aus dem SiGe-Plan ab.',
        );

        offeneStellen(l, 'baustelleneinrichtung');
    };

    const kapitelBaustellenordnung = () => {
        const [l, r] = zweiSpalten();
        const o = hb?.ordnung;

        bandUeberschrift(l, 'Arbeitszeiten und Zugang');
        feld(l, 'Arbeitszeiten', o?.arbeitszeiten);
        feld(l, 'Zugang', o?.zugang);
        feld(l, 'Sammelplatz', o?.sammelplatz);
        l.y += 3;

        bandUeberschrift(l, 'Regeln auf dieser Baustelle');
        const regeln = (o?.regeln || []).map(text).filter(Boolean);
        if (!regeln.length) {
            // Auflage: das Blatt darf gedruckt werden, muss aber sagen, dass
            // hier nichts steht. Eine leere Ueberschrift laese sich als „es
            // gibt keine Regeln" lesen.
            absatz(
                l,
                'Für diese Baustelle ist noch keine einzige Regel festgelegt. '
                + 'Solange gelten die Anweisungen der Bauleitung und die Unterweisung '
                + 'des jeweiligen Unternehmens.',
                'italic', SCHRIFT.block, FARBE.leise,
            );
        } else {
            punkte(l, regeln);
        }

        bandUeberschrift(r, 'Verhalten im Notfall');
        const schritte = (o?.notfallschritte || []).map(text).filter(Boolean);
        if (!schritte.length) {
            absatz(
                r,
                'Für diese Baustelle ist noch kein Ablauf für den Notfall festgelegt. '
                + 'Es gilt: Notruf 112, Unfallstelle sichern, Bauleitung verständigen.',
                'italic', SCHRIFT.block, FARBE.leise,
            );
        } else {
            punkte(r, schritte, '#');
        }
        r.y += 3;

        // Notrufnummern und Notfallstellen: gelesen, nicht erfasst. Der
        // Notrufplan gehoert dem SiGe-Plan (`sige_bloecke` typ='notrufplan').
        bandUeberschrift(r, 'Notruf und Notfallstellen');
        absatz(r, 'Rettungsdienst und Feuerwehr 112 · Polizei 110 · Giftnotruf 030 19240', 'bold', SCHRIFT.block);
        const notruf = daten.notrufplan;
        if (!notruf) {
            absatz(
                r,
                'Im SiGe-Plan ist kein Notrufplan hinterlegt. Krankenhaus und Durchgangsarzt '
                + 'sind dort einzutragen; dieses Handbuch trägt hier nichts ein, was nicht '
                + 'geprüft wurde.',
                'italic', SCHRIFT.klein, FARBE.rot,
            );
        } else {
            const stelle = (bezeichnung: string, k?: { name?: string; adresse?: string; telefon?: string }) => {
                const wert = [text(k?.name), text(k?.adresse), text(k?.telefon) && `Tel. ${text(k?.telefon)}`]
                    .filter(Boolean).join(', ');
                feld(r, bezeichnung, wert || 'im Notrufplan nicht eingetragen');
            };
            stelle('Krankenhaus', notruf.krankenhaus);
            stelle('Durchgangsarzt', notruf.durchgangsarzt);
            stelle('Augenarzt', notruf.augenarzt);
            const ersthelfer = (notruf.ersthelfer || [])
                .map(e => [text(e.name), text(e.firma)].filter(Boolean).join(' — '))
                .filter(Boolean);
            feld(r, 'Ersthelfer', ersthelfer.join(' · '));
        }

        offeneStellen(l, 'baustellenordnung');
    };

    const kapitelGefaehrdungsmatrix = () => {
        const b = neuerBereich(links, satzBreite);
        absatz(
            b,
            'Zeile = verursachendes Gewerk, Spalte = betroffenes Gewerk. Die Zahl in der Zelle '
            + 'ist die Zahl der gemeinsamen Kalendertage. Die Bewertung stammt aus dem Modul '
            + '„Gewerke-Überschneidung"; sie wird hier nur gedruckt.',
            'italic', SCHRIFT.klein, FARBE.leise,
        );

        const aktuelle = paare.filter(p => !p.veraltet_seit);
        if (gewerke.length < 2) {
            absatz(
                b,
                'Es sind weniger als zwei Gewerkeseiten angelegt. Eine Kreuzmatrix braucht '
                + 'mindestens zwei Gewerke — hier gibt es nichts zu kreuzen.',
                'italic', SCHRIFT.block, FARBE.leise,
            );
            offeneStellen(b, 'gefaehrdungsmatrix');
            return;
        }
        if (!aktuelle.length) {
            // Der wichtigste Satz dieses Kapitels: eine leere Matrix darf NIE
            // wie eine Entwarnung aussehen. Ohne Bewertung wird deshalb GAR
            // KEINE Matrix gezeichnet.
            absatz(
                b,
                'Für dieses Projekt liegt noch keine einzige bewertete Gewerkepaarung vor. '
                + 'Diese Seite zeigt deshalb bewusst keine Matrix: eine Kreuztabelle ohne '
                + 'Eintrag würde als „geprüft, keine gegenseitigen Gefährdungen" gelesen — '
                + 'und das wäre die gefährlichste Aussage, die dieses Handbuch treffen kann. '
                + 'Die Bewertung findet im Modul „Gewerke-Überschneidung" statt.',
                'italic', SCHRIFT.block, FARBE.rot,
            );
            offeneStellen(b, 'gefaehrdungsmatrix');
            return;
        }
        if (gewerke.length > MATRIX_MAX_GEWERKE) {
            absatz(
                b,
                `Dieses Projekt hat ${gewerke.length} Gewerkeseiten. Ab ${MATRIX_MAX_GEWERKE + 1} Gewerken `
                + 'ist eine Kreuzmatrix auf A3 nicht mehr sicher zu lesen; sie wird deshalb hier '
                + 'weggelassen. Die Paarungen stehen vollständig im Koordinierungsbericht.',
                'italic', SCHRIFT.block, FARBE.rot,
            );
            offeneStellen(b, 'gefaehrdungsmatrix');
            return;
        }

        const nachPaar = new Map<string, KoordinierungPaar[]>();
        aktuelle.forEach(p => {
            const key = paarKey(p.gewerk_a_norm || '', p.gewerk_b_norm || '');
            const vorhanden = nachPaar.get(key);
            if (vorhanden) vorhanden.push(p); else nachPaar.set(key, [p]);
        });

        const nameW = 60;
        const zellW = (satzBreite - nameW) / gewerke.length;
        const zeileH = Math.min(9, (b.unten - b.y - 24) / gewerke.length);
        const kopfY = b.y + 22;

        // Spaltenkoepfe stehen gedreht: waagerecht braeuchte jede Spalte die
        // Breite des laengsten Gewerkenamens, und dann passte keine Matrix
        // mehr auf das Blatt.
        setz(SCHRIFT.matrix, 'bold', FARBE.text);
        gewerke.forEach((g, i) => {
            doc.text(
                doc.splitTextToSize(g.gewerk_label, 20)[0] as string,
                links + nameW + i * zellW + zellW / 2,
                kopfY - 1.5,
                { angle: 60, align: 'left' },
            );
        });

        gewerke.forEach((zeile, i) => {
            const y = kopfY + i * zeileH;
            setz(SCHRIFT.matrix, 'bold', FARBE.text);
            (doc.splitTextToSize(zeile.gewerk_label, nameW - 2) as string[]).slice(0, 2).forEach((z, j) => {
                doc.text(z, links, y + 3 + j * 2.6);
            });
            const zeitA = zeitraumDerFassung(zeile);

            gewerke.forEach((spalte, j) => {
                const x = links + nameW + j * zellW;
                let fuellung = AMPEL_FARBE.grau;
                let inhalt = '';
                if (i === j) {
                    fuellung = [225, 225, 225];
                    inhalt = '—';
                } else if (koordinierungsSchluessel(zeile) === koordinierungsSchluessel(spalte)) {
                    // Zwei Seiten, die die Koordinierung als EIN Gewerk fuehrt.
                    fuellung = [235, 235, 235];
                    inhalt = '=';
                } else {
                    const zeitB = zeitraumDerFassung(spalte);
                    const fenster = berechneUeberschneidung(
                        { von: zeitA.von, bis: zeitA.bis },
                        { von: zeitB.von, bis: zeitB.bis },
                    );
                    const bewertungen = nachPaar.get(
                        paarKey(koordinierungsSchluessel(zeile), koordinierungsSchluessel(spalte)),
                    ) || [];
                    if (fenster) {
                        fuellung = AMPEL_FARBE[ampel(fenster.dauerTage)] || AMPEL_FARBE.grau;
                        inhalt = bewertungen.length ? String(fenster.dauerTage) : `${fenster.dauerTage}?`;
                    } else if (bewertungen.length) {
                        // Bewertet, aber heute keine gemeinsame Zeit mehr —
                        // die Bewertung bleibt sichtbar, sie ist der
                        // beweiskraeftige Teil.
                        fuellung = AMPEL_FARBE.orange;
                        inhalt = 'x';
                    }
                }
                doc.setFillColor(fuellung[0], fuellung[1], fuellung[2]);
                doc.setDrawColor(FARBE.linie[0], FARBE.linie[1], FARBE.linie[2]);
                doc.setLineWidth(0.1);
                doc.rect(x, y, zellW, zeileH, 'FD');
                if (inhalt) {
                    setz(SCHRIFT.matrix, 'normal', FARBE.text);
                    doc.text(inhalt, x + zellW / 2, y + zeileH / 2 + 1, { align: 'center' });
                }
            });
        });

        b.y = kopfY + gewerke.length * zeileH + 4;
        setz(SCHRIFT.klein, 'italic', FARBE.leise);
        if (passt(b, ZEILE.klein)) {
            b.y += ZEILE.klein;
            doc.text(
                'Zahl = gemeinsame Kalendertage · „?" = zeitliche Überschneidung, aber noch nicht bewertet '
                + '· „x" = bewertet, heute ohne gemeinsame Zeit · „=" = von der Koordinierung als ein Gewerk geführt '
                + `· rot ab 29 Tagen · ${aktuelle.length} Bewertung(en) aus koordinierung_paare`,
                links, b.y,
            );
        }
        offeneStellen(b, 'gefaehrdungsmatrix');
    };

    const kapitelGesamtzeitplan = () => {
        const b = neuerBereich(links, satzBreite);
        absatz(
            b,
            // Der Zusatz zur Freigabe steht ohne Bedingung da und wortgleich
            // auf der Bildschirmseite (`kapitel/Gesamtzeitplan.tsx`): er ist
            // in beiden Zustaenden wahr, und eine Fallunterscheidung im Text
            // waere eine zweite Stelle, an der Blatt und Bildschirm
            // auseinanderlaufen koennen.
            'Zeiträume aus den Arbeitsschritten des SiGe-Plans; ein am Handbuch gesetzter '
            + 'Zeitraum geht vor. Eine freigegebene Fassung zeigt die bei der Freigabe '
            + 'festgehaltenen Zeiträume. Schraffiert: Zeitfenster, in denen dieses Gewerk mit '
            + 'mindestens einem anderen zusammentrifft.',
            'italic', SCHRIFT.klein, FARBE.leise,
        );

        const alle = gewerke.map(g => alsZeitachsenGewerk(g, zeitraumDerFassung(g)));
        const mitZeitraum = alle.filter(g => g.von && g.bis && (g.von as string) <= (g.bis as string));
        const ohneZeitraum = alle.filter(g => !(g.von && g.bis && (g.von as string) <= (g.bis as string)));
        const achse = baueZeitachse(
            mitZeitraum,
            berechneAlleUeberschneidungen(mitZeitraum),
            projekt.bauzeitVon,
            projekt.bauzeitBis,
        );

        if (!achse) {
            absatz(
                b,
                'Kein einziges Gewerk trägt einen verwertbaren Zeitraum. Ein Terminplan lässt '
                + 'sich daraus nicht zeichnen — die Zeiträume kommen aus den Arbeitsschritten '
                + 'des SiGe-Plans.',
                'italic', SCHRIFT.block, FARBE.rot,
            );
        } else {
            const nameW = 58;
            const achseX = links + nameW;
            const achseW = satzBreite - nameW;
            const zeileH = Math.min(8, (b.unten - b.y - 30) / Math.max(1, achse.balken.length));
            const kopfY = b.y + 8;

            // Monatsraster. Die Spaltenbreite ist proportional zur echten
            // Monatslaenge (so liefert `baueZeitachse` die Anteile) — sonst
            // saesse der Balkenanfang unter dem falschen Monat.
            setz(SCHRIFT.matrix, 'normal', FARBE.leise);
            achse.monate.forEach(m => {
                const x = achseX + m.anteilVon * achseW;
                const w = (m.anteilBis - m.anteilVon) * achseW;
                strich(x, kopfY - 5, x, kopfY + achse.balken.length * zeileH, 0.1, FARBE.linie);
                // Kurze Monate bekommen keine Beschriftung — ein
                // uebereinandergedrucktes Raster ist unleserlich.
                if (w >= 6) doc.text(m.label, x + w / 2, kopfY - 1.5, { align: 'center' });
            });
            strich(achseX, kopfY, achseX + achseW, kopfY, 0.3, FARBE.linieStark);

            achse.balken.forEach((balken, i) => {
                const y = kopfY + i * zeileH;
                setz(SCHRIFT.matrix, 'normal', FARBE.text);
                doc.text(doc.splitTextToSize(balken.label, nameW - 3)[0] as string, links, y + zeileH * 0.7);
                const x = achseX + balken.anteilVon * achseW;
                const w = Math.max(0.6, (balken.anteilBis - balken.anteilVon) * achseW);
                doc.setFillColor(60, 90, 140);
                doc.rect(x, y + zeileH * 0.25, w, zeileH * 0.5, 'F');
                balken.zonen.forEach(z => {
                    doc.setFillColor(FARBE.rot[0], FARBE.rot[1], FARBE.rot[2]);
                    doc.rect(
                        achseX + z.anteilVon * achseW,
                        y + zeileH * 0.25,
                        Math.max(0.6, (z.anteilBis - z.anteilVon) * achseW),
                        zeileH * 0.5,
                        'F',
                    );
                });
                setz(SCHRIFT.matrix, 'normal', FARBE.leise);
                doc.text(`${tag(balken.von)} – ${tag(balken.bis)}`, achseX + achseW, y + zeileH * 0.7, { align: 'right' });
            });

            b.y = kopfY + achse.balken.length * zeileH + 5;
            absatz(b, `Zeitachse ${tag(achse.start)} bis ${tag(achse.ende)} · ${achse.monate.length} Monate`, 'italic', SCHRIFT.klein, FARBE.leise);
        }

        if (ohneZeitraum.length) {
            // Ohne Zeitraum heisst NICHT „faellt weg": diese Gewerke stehen
            // namentlich darunter. Sie zu uebergehen liesse den Zeitplan
            // vollstaendig aussehen.
            bandUeberschrift(b, 'Gewerke ohne verwertbaren Zeitraum');
            punkte(b, ohneZeitraum.map(g => `${g.label} — im SiGe-Plan ist kein Zeitraum hinterlegt.`));
        }

        offeneStellen(b, 'gesamtzeitplan');
    };

    const kapitelGewerk = (gewerkId?: string) => {
        const [l, r] = zweiSpalten();
        const g = gewerke.find(x => x.id === gewerkId);
        if (!g) {
            absatz(l, 'Diese Gewerkeseite wurde nicht gefunden.', 'italic', SCHRIFT.block, FARBE.rot);
            return;
        }
        const z = zeitraumDerFassung(g);

        bandUeberschrift(l, 'Ausführendes Unternehmen');
        feld(l, 'Gewerk', g.gewerk_label);
        feld(l, 'Firma', g.firma);
        feld(l, 'Verantwortlich', g.verantwortlich);
        feld(l, 'Telefon', g.telefon);
        feld(l, 'Beschäftigte', g.anzahl_beschaeftigte == null ? null : String(g.anzahl_beschaeftigte));
        feld(l, 'DIN 276', g.din_nummer);
        feld(l, 'Zeitraum', zeitraumText(z.von, z.bis));
        l.y += 2;

        // Die Schritte kommen ueber `schritteDesGewerks` und nicht ueber einen
        // zeichengleichen Vergleich mit `gewerk_label`. `gewerkeAusPlan`
        // buendelt mehrere Schreibweisen bewusst zu EINER Gewerkeseite
        // („Elektro" und „elektro "); der alte Vergleich fand davon nur eine
        // und druckte drei von fuenf Taetigkeiten. Nach einer Umbenennung des
        // Labels fand er gar keine mehr — und die Seite behauptete „kein
        // Arbeitsschritt" und „keine Gefährdungen".
        const eigene = schritteDesGewerks(g, daten.schritte || []);
        bandUeberschrift(l, 'Tätigkeiten laut SiGe-Plan');
        tabelle(
            l,
            ['Arbeitsschritt', 'Bereich', 'Abschnitt'],
            [4, 2, 2],
            eigene.map(s => [text(s.bezeichnung), text(s.raeumliche_zuordnung), text(s.hauptabschnitt)]),
            'Dem SiGe-Plan liegt für dieses Gewerk kein Arbeitsschritt bei.',
        );
        l.y += 2;

        bandUeberschrift(l, 'Gefährdungen und Schutzmaßnahmen');
        const zeilen: string[][] = [];
        eigene.forEach(s => {
            const gefs = (daten.gefaehrdungen || {})[s.id] || [];
            if (!gefs.length) {
                (s.schutzmassnahmen || []).forEach(m => zeilen.push([text(s.bezeichnung), '—', text(m), '']));
                return;
            }
            gefs.forEach(gef => {
                const mass = (daten.massnahmen || {})[gef.id] || [];
                if (!mass.length) {
                    zeilen.push([text(s.bezeichnung), text(gef.bezeichnung), '—', text(gef.prioritaet)]);
                    return;
                }
                mass.forEach(m => zeilen.push([
                    text(s.bezeichnung), text(gef.bezeichnung), text(m.beschreibung), text(gef.prioritaet),
                ]));
            });
        });
        tabelle(
            l,
            ['Arbeitsschritt', 'Gefährdung', 'Schutzmaßnahme', 'Risiko'],
            [3, 3, 4, 1.4],
            zeilen,
            'Für dieses Gewerk sind im SiGe-Plan keine Gefährdungen erfasst.',
        );

        bandUeberschrift(r, 'Persönliche Schutzausrüstung');
        tabelle(
            r,
            ['PSA', 'Norm', 'bei welcher Tätigkeit'],
            [3, 2, 4],
            (g.psa || []).map(p => [text(p.bezeichnung), text(p.norm), text(p.taetigkeiten)]),
            'Für dieses Gewerk ist keine PSA festgelegt.',
        );
        r.y += 2;

        bandUeberschrift(r, 'Anhang II BaustellV — besonders gefährliche Arbeiten');
        if ((g.anhang_ii || []).length) punkte(r, g.anhang_ii);
        else absatz(r, 'Für dieses Gewerk ist keine Arbeit nach Anhang II vermerkt.', 'italic', SCHRIFT.klein, FARBE.leise);
        r.y += 2;

        bandUeberschrift(r, 'Nachweise');
        const eigeneNachweise = nachweise.filter(n => n.handbuch_gewerk_id === g.id);
        tabelle(
            r,
            ['Nr.', 'Nachweis', 'Status', 'Frist'],
            [1, 5, 1.8, 1.8],
            eigeneNachweise.map(n => [
                text(n.nr),
                text(n.bezeichnung),
                text(n.status),
                n.frist ? tag(n.frist) : 'ohne Frist',
            ]),
            'Für dieses Gewerk ist kein Nachweis gefordert.',
        );
        r.y += 2;

        bandUeberschrift(r, 'Unterweisungen, Witterung, RAB 32');
        const zusatz: string[] = [
            ...(g.unterweisungen || []).map(u =>
                `Unterweisung: ${text(u.thema)}${text(u.intervall) ? ` (${text(u.intervall)})` : ''}`
                + `${text(u.nachweis) ? ` — ${text(u.nachweis)}` : ''}`),
            // Kein '→': die PDF-Standardschriften koennen den Pfeil nicht, und
            // ein einziges solches Zeichen kippt die ganze Zeile in UTF-16
            // (siehe `winAnsi.ts`). Der Gedankenstrich sagt dasselbe, steht in
            // CP1252 und passt zur Nachbarzeile ueber RAB 32.
            ...((g.witterung?.eintraege) || []).map(w => `Witterung: ${text(w.bedingung)} — Maßnahme: ${text(w.massnahme)}`),
            ...((g.rab32?.eintraege) || []).map(rr => `RAB 32: ${text(rr.beschreibung)} — ${text(rr.sicherheitskonzept)}`),
            ...((g.ordnung?.regeln) || []).map(x => `Zusätzliche Regel: ${text(x)}`),
            text(g.notfall?.ersthelfer) && `Ersthelfer des Gewerks: ${text(g.notfall?.ersthelfer)}`,
            text(g.notfall?.besonderheiten) && `Notfall-Besonderheit: ${text(g.notfall?.besonderheiten)}`,
        ].filter(Boolean) as string[];
        if (zusatz.length) punkte(r, zusatz);
        else absatz(r, 'Für dieses Gewerk sind keine gesonderten Festlegungen erfasst.', 'italic', SCHRIFT.klein, FARBE.leise);
    };

    const kapitelDokumentenverzeichnis = () => {
        const b = neuerBereich(links, satzBreite);
        const uebersicht = nachweisUebersicht(nachweise, heute);
        absatz(
            b,
            `${uebersicht.gesamt} Nachweis(e) · ${uebersicht.vorhanden} vorhanden · ${uebersicht.inPruefung} in Prüfung `
            + `· ${uebersicht.fehlt} fehlen · ${uebersicht.ueberfaellig} überfällig · ${uebersicht.bald} in den nächsten Tagen fällig `
            + `· ${uebersicht.ohneBeleg} gelten als vorhanden, sind aber keinem Dokument der Projektablage zugeordnet.`,
            'normal', SCHRIFT.block,
        );
        b.y += 2;

        const gewerkName = new Map(gewerke.map(g => [g.id, g.gewerk_label]));

        // ═══ DAS ADMIN-SCHLOSS ═════════════════════════════════════════════
        // Zusammengestellt wird mit `baueVerzeichnis` — derselben geprueften
        // Funktion, die das Kapitelblatt benutzt. Hier stand einmal eine
        // eigene Zusammenstellung, die `nur_admin` GAR NICHT gelesen hat: ein
        // per Admin-Schloss gesperrtes Dokument erschien damit mit Titel und
        // Dateinamen auf dem Verzeichnis, das dem Unternehmer ausgehaendigt
        // wird. Ein gesperrtes Dokument erscheint ausschliesslich als ZAHL in
        // der Fussnote: Verschweigen waere falsch (dann fehlte unbemerkt
        // etwas), Auflisten waere schlimmer (dann stuende der gesperrte Inhalt
        // auf Papier).
        const verzeichnis = baueVerzeichnis<ProjektDokument>(
            daten.dokumente || [], nachweise, kategorieLabel,
        );
        const nachweisText = (n: HandbuchNachweis): string =>
            `${gewerkName.get(n.handbuch_gewerk_id) || '—'}: ${text(n.bezeichnung)} (${text(n.status)})`;

        bandUeberschrift(b, 'Abgelegte Dokumente');
        tabelle(
            b,
            ['Titel', 'Kategorie', 'Stand', 'gültig bis', 'Dateiname', 'Nachweis'],
            [3.4, 1.8, 1.2, 1.4, 2.8, 3.4],
            verzeichnis.eintraege.map(e => [
                text(e.dokument.titel) + (e.ueberModul ? '' : ' (nur über einen Nachweis verknüpft)'),
                kategorieLabel(e.dokument.kategorie || ''),
                text(e.dokument.stand),
                e.dokument.gueltig_bis ? tag(e.dokument.gueltig_bis) : '',
                text(e.dokument.dateiname),
                e.nachweise.length
                    ? e.nachweise.map(nachweisText).join(' · ')
                    : 'keinem Nachweis zugeordnet',
            ]),
            'Der Projektablage ist noch kein Dokument mit Bezug zum Handbuch zugeordnet.',
        );
        b.y += 2;

        const offen = offeneNachweise(nachweise);
        bandUeberschrift(b, 'Geforderte Nachweise ohne hinterlegtes Dokument');
        tabelle(
            b,
            ['Gewerk', 'Nr.', 'Nachweis', 'Status', 'Frist', 'Bemerkung'],
            [2.4, 0.8, 4, 1.4, 1.4, 2.4],
            offen.map(n => [
                gewerkName.get(n.handbuch_gewerk_id) || '—',
                text(n.nr),
                text(n.bezeichnung),
                text(n.status),
                n.frist ? tag(n.frist) : 'ohne Frist',
                // Der unangenehmste Fall: es sieht erledigt aus und ist nicht
                // belegbar.
                n.status === 'vorhanden'
                    ? 'als vorhanden geführt, ohne Beleg in der Ablage'
                    : text(n.bemerkung),
            ]),
            nachweise.length
                ? 'Zu jedem geforderten Nachweis liegt ein Dokument in der Ablage.'
                : 'Es ist noch kein Nachweis gefordert — die Gewerkeseiten sind hierzu leer.',
        );

        // Fussnoten: alles, was dieses Verzeichnis unvollstaendig macht, wird
        // BENANNT — mit Zahl, ohne Inhalt.
        if (verzeichnis.gesperrt > 0) {
            b.y += 2;
            absatz(
                b,
                `${verzeichnis.gesperrt} zugehörige(s) Dokument(e) trägt/tragen ein Admin-Schloss und `
                + 'ist/sind deshalb hier weder mit Titel noch mit Dateinamen aufgeführt. Einsicht nur '
                + 'über die Projektablage.',
                'italic', SCHRIFT.klein, FARBE.orange,
            );
        }
        if (verzeichnis.belegFehlt.length > 0) {
            absatz(
                b,
                `${verzeichnis.belegFehlt.length} Nachweis(e) verweisen auf ein Dokument, das in der `
                + `Ablage nicht (mehr) erreichbar ist: ${verzeichnis.belegFehlt.map(n => text(n.bezeichnung)).join(', ')}.`,
                'italic', SCHRIFT.klein, FARBE.rot,
            );
        }

        offeneStellen(b, 'dokumentenverzeichnis');
    };

    const kapitelHistorie = () => {
        const [l, r] = zweiSpalten();

        bandUeberschrift(l, 'Fassungen dieses Handbuchs');
        tabelle(
            l,
            ['Nr.', 'Datum', 'Autor', 'Änderungen', 'Fassung'],
            [1, 1.6, 2.4, 5, 1.8],
            (hb?.historie || []).map(h => [
                text(h.nr), tag(h.datum), text(h.autor), text(h.aenderungen), text(h.version_label),
            ]),
            'Es ist noch keine Fassung vermerkt.',
        );

        bandUeberschrift(r, 'Fassungen des SiGe-Plans');
        if (daten.planUngeladen) {
            // „Keine Planfassung" und „nicht nachgesehen" sehen gleich aus und
            // meinen Gegenteiliges.
            absatz(
                r,
                'Die Planfassungen wurden zu diesem Druck nicht geladen. Diese Liste ist deshalb '
                + 'leer, ohne dass daraus folgt, dass es keine gibt.',
                'italic', SCHRIFT.block, FARBE.leise,
            );
        } else {
            tabelle(
                r,
                ['Plan-Nr.', 'Fassung', 'angelegt am'],
                [2, 3, 2],
                (daten.planFassungen || []).map(f => [
                    text(f.plan_nr), text(f.version_label), tag((f.created_at || '').slice(0, 10)),
                ]),
                'Zu diesem Projekt ist keine Planfassung hinterlegt.',
            );
        }
        r.y += 2;
        absatz(
            r,
            'Eine Freigabe friert die Kapitel ein. Eine spätere Änderung erzeugt eine NEUE '
            + 'Fassung in dieser Liste und überschreibt die freigegebene nicht.',
            'italic', SCHRIFT.klein, FARBE.leise,
        );

        offeneStellen(l, 'historie');
    };

    // ── Seiten setzen ───────────────────────────────────────────────────

    seiten.forEach((eintrag, i) => {
        if (i > 0) doc.addPage([GEO.breite, GEO.hoehe], 'landscape');
        kopfZeichnen(eintrag);
        fussZeichnen();

        switch (eintrag.kapitel) {
            case 'deckblatt': kapitelDeckblatt(); break;
            case 'organigramm': kapitelOrganigramm(); break;
            case 'baustelleneinrichtung': kapitelBaustelleneinrichtung(); break;
            case 'baustellenordnung': kapitelBaustellenordnung(); break;
            case 'gefaehrdungsmatrix': kapitelGefaehrdungsmatrix(); break;
            case 'gesamtzeitplan': kapitelGesamtzeitplan(); break;
            case 'gewerke': kapitelGewerk(eintrag.gewerkId); break;
            case 'dokumentenverzeichnis': kapitelDokumentenverzeichnis(); break;
            case 'historie': kapitelHistorie(); break;
            default: break;
        }
    });
};

/**
 * Das SiGe-Handbuch als jsPDF-Dokument.
 *
 * Die Seitenzahl ist immer `seitenPlan(lage).length` — auch bei einem
 * Handbuch, das gerade erst angelegt wurde. Ein halbes Dokument mit
 * ausgelassenen Kapiteln waere schlimmer als ein vollstaendiges, dessen leere
 * Stellen sagen, dass sie leer sind.
 */
export const generateHandbuchPdf = (
    daten: HandbuchPdfDaten,
    optionen?: HandbuchPdfOptionen,
): jsPDF => {
    const doc = optionen?.__docFactory
        ? optionen.__docFactory()
        : new jsPDF({
            unit: 'mm',
            orientation: 'landscape',
            format: [...HANDBUCH_MASSE],
            compress: true,
        });
    // Letzte Rueckfallebene fuer alles, was nicht durch `text()` gelaufen ist.
    sichereTextausgabe(doc);
    renderHandbuch(doc, daten);
    return doc;
};

/** Die Kapitel in Druckreihenfolge — fuer Aufrufer, die eine Vorschau bauen.
 *  Bewusst nur durchgereicht: die Reihenfolge gehoert `handbuchLogic.ts`. */
export const handbuchKapitel = KAPITEL;

/**
 * Dateiname des Handbuchs.
 *
 * Ohne Sonderzeichen und ohne Leerzeichen: der Name landet auch als
 * Objektname im Storage-Bucket, und dort ist ein Schraegstrich aus einem
 * Projektnamen ein neuer Ordner.
 */
export const handbuchDateiname = (
    projektName: string,
    fassung?: string | null,
    datumIso?: string,
): string => {
    const sauber = (s: string) => (s || '')
        .replace(/[^\wÄÖÜäöüß\- ]+/g, '')
        .trim()
        .replace(/\s+/g, '_')
        .slice(0, 60);
    const teile = ['SiGe-Handbuch', sauber(projektName)];
    if (text(fassung)) teile.push(`Fassung_${sauber(text(fassung))}`);
    teile.push((datumIso || '').slice(0, 10) || 'ohne-Datum');
    return `${teile.filter(Boolean).join('_')}.pdf`;
};
